import { format } from 'date-fns';
import { nl } from 'date-fns/locale';
import { Calendar, User, MapPin, FolderKanban, Briefcase, Link as LinkIcon, Edit, AlertCircle } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { UnifiedTodo, getPriorityColor, getStatusBadge, isTodoBlocked } from '@/lib/todoHelpers';

interface TodoCardProps {
  todo: UnifiedTodo;
  allTodos: UnifiedTodo[];
  onToggleComplete: (todoId: string, currentStatus: string) => void;
  onEdit: (todo: UnifiedTodo) => void;
  onSelect?: (todoId: string, selected: boolean) => void;
  isSelected?: boolean;
  selectionMode?: boolean;
  showContext?: boolean;
  compact?: boolean;
}

export function TodoCard({
  todo,
  allTodos,
  onToggleComplete,
  onEdit,
  onSelect,
  isSelected,
  selectionMode = false,
  showContext = false,
  compact = false,
}: TodoCardProps) {
  const isBlocked = isTodoBlocked(todo, allTodos);
  const statusBadge = getStatusBadge(isBlocked ? 'blocked' : todo.status);
  const isOverdue = todo.due_date && 
    new Date(todo.due_date) < new Date() && 
    todo.status !== 'completed';

  return (
    <div 
      className={cn(
        'flex items-start gap-2 sm:gap-3 rounded-lg border transition-colors',
        compact ? 'p-2' : 'p-3',
        isSelected ? 'bg-primary/5 border-primary/30' : 'hover:bg-muted/50',
        todo.status === 'completed' && 'opacity-60'
      )}
    >
      {/* Single action element - either selection checkbox OR completion trigger */}
      {selectionMode ? (
        // Selection mode: square checkbox for bulk selection
        <Checkbox
          checked={isSelected}
          onCheckedChange={(checked) => onSelect?.(todo.id, checked as boolean)}
          className="mt-1 min-w-[20px] min-h-[20px]"
          onClick={(e) => e.stopPropagation()}
        />
      ) : (
        // Normal mode: circle for completion
        <button
          onClick={(e) => {
            e.stopPropagation();
            if (!isBlocked) onToggleComplete(todo.id, todo.status);
          }}
          disabled={isBlocked}
          className={cn(
            "mt-1 min-w-[20px] min-h-[20px] rounded-full border-2 flex items-center justify-center transition-colors",
            todo.status === 'completed' 
              ? "bg-primary border-primary text-primary-foreground" 
              : "border-muted-foreground/40 hover:border-primary hover:bg-primary/10",
            isBlocked && "opacity-50 cursor-not-allowed"
          )}
        >
          {todo.status === 'completed' && (
            <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          )}
        </button>
      )}

      {/* Content */}
      <div className="flex-1 min-w-0" onClick={() => onEdit(todo)}>
        <div className="flex items-start justify-between gap-2 cursor-pointer">
          <div className="flex-1 min-w-0">
            <h3 className={cn(
              'font-medium text-sm sm:text-base',
              todo.status === 'completed' && 'line-through text-muted-foreground'
            )}>
              {todo.title}
            </h3>
            
            {/* Context info */}
            {showContext && (
              <div className="flex items-center gap-2 mt-1 flex-wrap">
                {todo.charging_locations && (
                  <div className="flex items-center gap-1 text-xs text-muted-foreground">
                    <MapPin className="h-3 w-3" />
                    <span className="truncate">{todo.charging_locations.name}</span>
                  </div>
                )}
                {todo.projects && (
                  <div className="flex items-center gap-1 text-xs text-muted-foreground">
                    <FolderKanban className="h-3 w-3" />
                    <span className="truncate">{todo.projects.name}</span>
                  </div>
                )}
                {todo.charging_opportunities && (
                  <div className="flex items-center gap-1 text-xs text-muted-foreground">
                    <Briefcase className="h-3 w-3" />
                    <span className="truncate">{todo.charging_opportunities.name}</span>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Edit button */}
          <Button
            variant="ghost"
            size="sm"
            className="min-w-[36px] shrink-0 h-8 w-8 p-0"
            onClick={(e) => {
              e.stopPropagation();
              onEdit(todo);
            }}
          >
            <Edit className="h-4 w-4" />
          </Button>
        </div>

        {/* Description */}
        {!compact && todo.description && (
          <p className="text-xs sm:text-sm text-muted-foreground mt-1 line-clamp-2 whitespace-pre-wrap">
            {todo.description}
          </p>
        )}

        {/* Badges and metadata */}
        <div className="flex items-center gap-1.5 sm:gap-2 mt-2 flex-wrap">
          <Badge className={`text-xs ${getPriorityColor(todo.priority)}`}>
            {todo.priority}
          </Badge>
          <Badge variant={statusBadge.variant} className="text-xs">
            {statusBadge.label}
          </Badge>
          
          {isOverdue && (
            <Badge variant="destructive" className="text-xs gap-1">
              <AlertCircle className="h-3 w-3" />
              Verlopen
            </Badge>
          )}

          {isBlocked && todo.parent_todo && (
            <Badge variant="outline" className="text-xs">
              <LinkIcon className="h-3 w-3 mr-1" />
              <span className="hidden sm:inline">Wacht op: {todo.parent_todo.title}</span>
              <span className="sm:hidden">Geblokkeerd</span>
            </Badge>
          )}

          {todo.due_date && !isOverdue && (
            <div className="flex items-center gap-1 text-xs text-muted-foreground">
              <Calendar className="h-3 w-3" />
              <span className="hidden sm:inline">
                {format(new Date(todo.due_date), 'dd MMM yyyy', { locale: nl })}
              </span>
              <span className="sm:hidden">
                {format(new Date(todo.due_date), 'dd MMM', { locale: nl })}
              </span>
            </div>
          )}

          {todo.profiles && (
            <div className="flex items-center gap-1 text-xs text-muted-foreground truncate max-w-[150px]">
              <User className="h-3 w-3 shrink-0" />
              <span className="truncate">{todo.profiles.display_name || todo.profiles.email}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
